import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import moment from 'moment';
import { useRangeSelection } from './useRangeSelection';
import { PRESETS, DEFAULT_PRESET } from './presets';
import './date-range-picker.css';

const fmt = (d) => (d ? moment(d).format('DD/MM/YYYY') : '');
// The start/end panel inputs use the long human form ("August 7, 2026") per the
// ticket, while the trigger keeps the compact DD/MM/YYYY – DD/MM/YYYY form.
const fmtLong = (d) => (d ? moment(d).format('MMMM D, YYYY') : '');
// The end input reads the literal word "Today" when its date is the current day,
// per the ticket spec; every other day falls back to the long form.
const fmtEnd = (d) => {
  if (!d) return '';
  return moment(d).isSame(new Date(), 'day') ? 'Today' : fmtLong(d);
};
const NARROW_QUERY = '(max-width: 640px)';

/**
 * Tracks whether the viewport currently matches the narrow (mobile) breakpoint,
 * so the calendar can collapse from dual-month to single-month layout. Falls back
 * to `false` (dual-month) in environments without `window.matchMedia` (e.g. jsdom
 * in tests that don't stub it), rather than throwing.
 *
 * @param {string} query - The media query to watch.
 * @returns {boolean} Whether the query currently matches.
 */
const useMediaQuery = (query) => {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [matches, setMatches] = useState(() => (supported ? window.matchMedia(query).matches : false));

  useEffect(() => {
    if (!supported) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query, supported]);

  return matches;
};

/**
 * Dual-month date range picker with a text-input trigger and a confirm/cancel flow.
 * `onChange` only fires when the user confirms the selection (Confirm button or
 * clicking outside the dropdown) — never while a selection is in progress.
 *
 * @param {Object} props
 * @param {{start: Date, end: Date}} props.value - The currently confirmed range.
 * @param {(range: {start: Date, end: Date, preset: string}) => void} props.onChange - Called on
 *   confirm/click-outside with the new range PLUS the preset key it came from
 *   ('this_week', 'this_month', 'year_to_date', 'custom', ...). The key is what
 *   tells the backend which period to compare against, so it must travel with
 *   the range rather than being derived again downstream.
 * @param {Date} [props.maxDate] - Latest selectable date; dates after this are disabled.
 * @param {number} [props.weekStartsOn] - Day the calendar GRID starts on for display
 *   (0 = Sunday, per the Figma design). This is cosmetic only — the week/month
 *   preset ranges are always computed Monday–Sunday (the finance week) in presets.js.
 * @param {boolean} [props.loading] - When true, disables the trigger and shows a loading indicator.
 * @returns {JSX.Element}
 */
export default function DateRangePicker({
  value, onChange, maxDate = new Date(), weekStartsOn = 0, loading = false, weekOnly = false,
}) {
  const [open, setOpen] = useState(false);
  const isNarrow = useMediaQuery(NARROW_QUERY);

  // When `weekOnly` is set (e.g. Fleet Overview, whose backend is Mon–Sun week
  // based and cannot aggregate arbitrary ranges), the picker is constrained to
  // whole finance weeks: the Monthly / Year-to-Date presets are hidden, and any
  // day click snaps to that day's Monday–Sunday week rather than free-form range
  // selection. Default (false) leaves the full range picker unchanged.
  const WEEK_ONLY_KEYS = new Set(['this_week', 'last_week', 'custom']);
  const availablePresets = weekOnly
    ? PRESETS.filter((preset) => WEEK_ONLY_KEYS.has(preset.key))
    : PRESETS;
  const weekOf = (day) => ({
    start: moment(day).startOf('isoWeek').toDate(),
    end: moment(day).endOf('isoWeek').toDate(),
  });

  /**
   * Clamps a computed range's end to `maxDate` so no preset can ever produce a
   * future end date (e.g. This Month → end of month can spill past today). The
   * start is always in the past for every preset, so only the end needs guarding.
   *
   * @param {{start: Date, end: Date}} range - The raw computed range.
   * @returns {{start: Date, end: Date}} The range with its end clamped to maxDate.
   */
  const clampToMax = (range) => ({ start: range.start, end: range.end > maxDate ? maxDate : range.end });

  /**
   * Computes a preset's range for "now" with the end clamped to maxDate. This is
   * the single source of truth for both applying a preset and matching an incoming
   * value against the presets, so the two paths always agree on the clamped bounds.
   *
   * @param {{range: ((today: Date) => {start: Date, end: Date})|null}} preset
   * @returns {{start: Date, end: Date}} The clamped range for the preset.
   */
  const rangeForPreset = (preset) => clampToMax(preset.range(new Date()));

  /**
   * Derives which preset key a confirmed range corresponds to by matching its
   * start and end (same-day) against each preset's clamped range. Returns 'custom'
   * when the range matches no preset.
   *
   * @param {{start: Date, end: Date}} range - The range to classify.
   * @returns {string} The matching preset key, or 'custom'.
   */
  const derivePreset = (range) => {
    if (!range.start || !range.end) return 'custom';
    const match = availablePresets.find((preset) => preset.range
      && moment(range.start).isSame(rangeForPreset(preset).start, 'day')
      && moment(range.end).isSame(rangeForPreset(preset).end, 'day'));
    return match ? match.key : 'custom';
  };

  // On first-ever mount with no confirmed value yet, default to This Week's range
  // rather than an undefined/empty range.
  const initialRange = value || rangeForPreset(PRESETS.find((preset) => preset.key === DEFAULT_PRESET));
  const initialPreset = derivePreset(initialRange);
  const [confirmed, setConfirmed] = useState(initialRange);
  // The rail highlight reflects the confirmed value on mount (derived from it),
  // not a hard-coded default, so a remount honours the controlled `value` prop.
  const [activePreset, setActivePreset] = useState(initialPreset);
  // Tracks which preset was active at the time of the last confirm, so Cancel/Escape
  // can revert the rail highlight to match the reverted (confirmed) range instead of
  // leaving a stale preset highlighted.
  const [confirmedPreset, setConfirmedPreset] = useState(initialPreset);
  // The displayed calendar month is controlled so that selecting a preset (or
  // opening the picker) navigates the calendar to the relevant range, rather
  // than staying on a static initial month.
  const [month, setMonth] = useState(initialRange.start);
  const {
    draft, activeInput, setActiveInput, onDayClick, onDayHover, onDayLeave, hoverPreview, reset,
  } = useRangeSelection(initialRange);
  const ref = useRef(null);

  // Honour the controlled `value` prop: when the parent supplies a different
  // confirmed range (compared by day, since it recreates the object each render),
  // sync it into the confirmed + draft state and re-derive the rail highlight.
  // The panel is left untouched while open so an in-progress edit is not clobbered.
  useEffect(() => {
    if (!value) return;
    const sameByDay = moment(value.start).isSame(confirmed.start, 'day')
      && moment(value.end).isSame(confirmed.end, 'day');
    if (sameByDay) return;
    const preset = derivePreset(value);
    setConfirmed(value);
    setConfirmedPreset(preset);
    if (!open) {
      reset(value);
      setActivePreset(preset);
      setMonth(value.start);
    }
  }, [value]);

  const cancel = () => { reset(confirmed); setActivePreset(confirmedPreset); setOpen(false); };
  // Confirming with no start (e.g. the user picked Custom, which clears it) must
  // not ship an invalid range: fall back to the previously confirmed range instead.
  const confirm = () => {
    if (!draft.start) { cancel(); return; }
    setConfirmed(draft); setConfirmedPreset(activePreset);
    onChange({ ...draft, preset: activePreset });
    setOpen(false);
  };
  // Opening the picker navigates the calendar to the confirmed range's month and
  // drops any hover preview so a stale highlight from a prior session cannot survive
  // the close/reopen.
  const openPicker = () => { if (loading) return; onDayLeave(); setMonth(confirmed.start); setOpen(true); };

  // Manual day selection means the user is no longer following a preset. Outside
  // (overflow) days belong to an adjacent month and must not be selectable, so a
  // click on one is ignored rather than silently flipping the range to Custom.
  const handleDayClick = (day, modifiers) => {
    if (modifiers.outside) return;
    // Week-only: a click selects the whole Monday–Sunday week containing the day
    // (clamped to maxDate), replacing the draft, rather than setting one endpoint.
    if (weekOnly) {
      const wk = clampToMax(weekOf(day));
      reset(wk);
      setActivePreset(derivePreset(wk));
      setMonth(wk.start);
      return;
    }
    setActivePreset('custom');
    onDayClick(day);
  };

  /**
   * Opens the dropdown from the keyboard when the (readOnly) trigger input is
   * focused, since a readOnly input never fires a native click from Enter/Space.
   *
   * @param {KeyboardEvent} e - The keydown event on the trigger input.
   * @returns {void}
   */
  const handleTriggerKeyDown = (e) => {
    if (loading) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      openPicker();
    }
  };

  // A day counts as "today, outside the current selection" when it's today but
  // falls outside the in-progress draft range — it gets the today token instead
  // of the in-range/selected tokens.
  const isTodayOutsideSelection = (day) => {
    const isToday = moment(day).isSame(new Date(), 'day');
    if (!isToday) return false;
    const withinDraft = draft.start && draft.end && moment(day).isBetween(draft.start, draft.end, 'day', '[]');
    return !withinDraft;
  };

  /**
   * Resolves which endpoint a click on `day` would update and the range it would
   * produce, mirroring the smart-targeting logic in `useRangeSelection.onDayClick`:
   * a day earlier than the start becomes the new start, a day later than the end
   * becomes the new end, otherwise the active input is filled.
   *
   * @param {Date} day - The candidate day (hovered or clicked).
   * @returns {{field: 'start'|'end', from: Date, to: Date}} The target input and previewed range.
   */
  const smartTarget = (day) => {
    const { start, end } = draft;
    if (start && day < start) return { field: 'start', from: day, to: end || day };
    if (end && day > end) return { field: 'end', from: start || day, to: day };
    if (activeInput === 'start') return { field: 'start', from: day, to: end || day };
    return { field: 'end', from: start || day, to: day };
  };

  // Hover preview range: the days between the endpoint the click would keep and the
  // hovered day preview the range the user is about to create, following the same
  // smart-targeting rules as an actual click.
  const hoverPreviewRange = () => {
    if (!hoverPreview) return [];
    const { from, to } = smartTarget(hoverPreview);
    if (!from || !to) return [];
    // Wrap the range in an array so the function's return type is consistent with
    // the empty-preview cases above; react-day-picker accepts a matcher array and
    // highlights the same days as the bare range object.
    return from <= to ? [{ from, to }] : [{ from: to, to: from }];
  };

  // While hovering, the input that a click WOULD update carries the active
  // highlight, per the smart-targeting spec; with no hover it falls back to the
  // real active input.
  const hoverField = hoverPreview ? smartTarget(hoverPreview).field : null;
  const startInputActive = hoverField ? hoverField === 'start' : activeInput === 'start';
  const endInputActive = hoverField ? hoverField === 'end' : activeInput === 'end';

  /**
   * Applies a preset's clamped range to the draft + calendar (via the hook's
   * `reset`) without touching the confirmed trigger value. Selecting `custom`
   * clears the draft start so the user must pick a fresh start day, leaving the
   * draft end in place and the start input active.
   *
   * @param {{key: string, range: ((today: Date) => {start: Date, end: Date})|null}} preset
   * @returns {void}
   */
  const selectPreset = (preset) => {
    setActivePreset(preset.key);
    if (preset.key === 'custom') {
      // Week-only: Custom keeps the current week highlighted and lets the user
      // click any day to jump to that day's Mon–Sun week, so it must NOT clear the
      // draft into the two-click start/end flow used by the full range picker.
      if (weekOnly) return;
      reset({ start: null, end: draft.end });
      return;
    }
    if (preset.range) {
      const range = rangeForPreset(preset);
      reset(range);
      setMonth(range.start);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) confirm(); };
    const onKey = (e) => { if (e.key === 'Escape') cancel(); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
  });

  return (
    <div ref={ref} className="pp-drp-trigger-wrap">
      <input
        data-testid="date-range-trigger"
        className="pp-drp-trigger"
        readOnly
        disabled={loading}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Date range"
        onClick={openPicker}
        onKeyDown={handleTriggerKeyDown}
        value={`${fmt(confirmed.start)} – ${fmt(confirmed.end)}`}
      />
      {loading && (
        <span className="pp-spinner" role="status" aria-label="Loading" data-testid="date-range-loading" />
      )}
      {open && (
        <div className="pp-drp" role="dialog" aria-label="Select date range">
          <div className="pp-drp-presets" role="group" aria-label="Date range presets">
            {availablePresets.map((preset) => (
              <button
                key={preset.key}
                type="button"
                data-testid={`date-range-preset-${preset.key.replaceAll('_', '-')}`}
                aria-pressed={activePreset === preset.key}
                className={`pp-preset${activePreset === preset.key ? ' pp-preset--active' : ''}`}
                onClick={() => selectPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="pp-drp-main">
            <fieldset
              className="pp-inputs-row"
              aria-label="Selected dates"
              style={{ border: 'none', margin: 0, padding: 0, minInlineSize: 'auto' }}
            >
              <input
                data-testid="input-start"
                readOnly
                aria-label="Start date"
                placeholder="Pick a start date"
                aria-current={activeInput === 'start' ? 'true' : undefined}
                className={`pp-date-input${startInputActive ? ' pp-input--active' : ''}`}
                value={fmtLong(draft.start)}
                onFocus={() => { setActiveInput('start'); setActivePreset('custom'); }}
                onClick={() => { setActiveInput('start'); setActivePreset('custom'); }}
              />
              <span className="pp-inputs-arrow" aria-hidden="true">→</span>
              <input
                data-testid="input-end"
                readOnly
                aria-label="End date"
                placeholder="Pick an end date"
                aria-current={activeInput === 'end' ? 'true' : undefined}
                className={`pp-date-input${endInputActive ? ' pp-input--active' : ''}`}
                value={fmtEnd(draft.end)}
                onFocus={() => { setActiveInput('end'); setActivePreset('custom'); }}
                onClick={() => { setActiveInput('end'); setActivePreset('custom'); }}
              />
            </fieldset>
            <div aria-live="polite" className="pp-visually-hidden">
              {`Selected range ${fmt(draft.start)} to ${fmt(draft.end)}`}
            </div>
            <DayPicker mode="range" numberOfMonths={isNarrow ? 1 : 2} weekStartsOn={weekStartsOn}
              month={month} onMonthChange={setMonth}
              selected={{ from: draft.start, to: draft.end }}
              disabled={{ after: maxDate }} showOutsideDays
              modifiers={{ todayOutside: isTodayOutsideSelection, hoverPreview: hoverPreviewRange() }}
              modifiersClassNames={{ todayOutside: 'pp-today-outside', hoverPreview: 'pp-hover-preview' }}
              onDayClick={handleDayClick}
              onDayMouseEnter={(day, modifiers) => { if (!modifiers.outside) onDayHover(day); }}
              onDayMouseLeave={onDayLeave} />
            <div className="pp-drp-actions">
              <button type="button" className="pp-btn pp-btn--cancel" onClick={cancel}>Cancel</button>
              <button type="button" className="pp-btn pp-btn--confirm" onClick={confirm}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
